import { useEffect, useState, useCallback } from "react";
import { Loader2, ClipboardCheck, CheckCircle2, XCircle, Pencil, User, ShoppingCart, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(0)}`);
const fmt = (s) => (s || "").slice(0, 16).replace("T", " ") || "—";
const STATUS = [
  { id: "PENDIENTE_APROBACION", label: "Pendientes" },
  { id: "APROBADA", label: "Aprobadas" },
  { id: "RECHAZADA", label: "Rechazadas" },
];
const BADGE = {
  PENDIENTE_APROBACION: "bg-amber-100 text-amber-800",
  APROBADA: "bg-emerald-100 text-emerald-700",
  RECHAZADA: "bg-rose-100 text-rose-700",
};
const MODES = [
  { v: "FIXED_OWNER_PRICE", l: "Precio fijo del dueño" },
  { v: "COST_PLUS_PROFIT_SHARE", l: "Costo + % ganancia" },
  { v: "POST_SALE_SETTLEMENT", l: "Liquidación posterior" },
];

export function ApprovalsTab() {
  const [status, setStatus] = useState("PENDIENTE_APROBACION");
  const [ops, setOps] = useState(null);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    setOps(null);
    adminApi.get(`/admin/approvals?status=${status}`).then((r) => setOps(r.data || [])).catch(() => { setOps([]); toast.error("No se pudieron cargar las operaciones"); });
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="approvals-panel">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2"><ClipboardCheck className="w-6 h-6" /> Operaciones por aprobar</h1>
        <p className="text-sm text-zinc-500 mt-1">Revisa, corrige y aprueba las operaciones registradas por vendedores. Las ventas pendientes no acreditan a los consignadores hasta ser aprobadas.</p>
      </div>
      <div className="flex gap-2">
        {STATUS.map((s) => (
          <button key={s.id} onClick={() => setStatus(s.id)} data-testid={`approvals-status-${s.id}`}
            className={`h-9 rounded-full px-4 text-sm font-medium transition-colors ${status === s.id ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {ops === null ? (
        <div className="py-12 grid place-items-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : ops.length === 0 ? (
        <p className="py-10 text-center text-sm text-zinc-400" data-testid="approvals-empty">No hay operaciones {STATUS.find((s) => s.id === status)?.label.toLowerCase()}.</p>
      ) : (
        <div className="space-y-3">
          {ops.map((op) => (
            <div key={op.operation_id} className="rounded-2xl border border-zinc-200 bg-white p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`approval-op-${op.operation_id}`}>
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-900">
                    {op.kind === "TRADE_IN" ? <RotateCcw className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}{op.operation_id}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${BADGE[op.approval_status] || "bg-zinc-100"}`}>{op.approval_status?.replace("_", " ")}</span>
                  <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-600">{op.kind === "TRADE_IN" ? "TRADE-IN" : "VENTA"}</span>
                </div>
                <p className="text-[12px] text-zinc-600 break-words">{(op.lines || []).map((l) => l.label).filter(Boolean).join(" · ") || "—"}</p>
                <p className="text-[11px] text-zinc-400 flex items-center gap-1"><User className="w-3 h-3" /> {op.registered_by || "—"} · {fmt(op.registered_at)} {op.customer_name ? `· Cliente: ${op.customer_name}` : ""}</p>
              </div>
              <div className="flex items-center gap-2 sm:shrink-0">
                {op.kind !== "TRADE_IN" && <span className="text-sm font-semibold text-zinc-900">{money(op.total)}</span>}
                <Button size="sm" variant="outline" className="h-9 rounded-lg" onClick={() => setSel(op)} data-testid={`approval-review-${op.operation_id}`}><Pencil className="w-4 h-4 mr-1" /> Revisar</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {sel && <ReviewModal op={sel} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); }} />}
    </div>
  );
}

function ReviewModal({ op, onClose, onDone }) {
  const isTI = op.kind === "TRADE_IN";
  const [form, setForm] = useState(() => ({
    customer_name: op.customer_name || "", customer_phone: op.customer_phone || "", customer_doc: op.customer_doc || "",
    payment_method: op.payment_method || "", sale_date: (op.sale_date || "").slice(0, 10), notes: op.notes || "",
    agreed_value: op.agreed_value ?? "",
    lines: (op.lines || []).map((l) => ({
      source: l.source, id: l.id, model: l.model || "", capacity: l.capacity || "", color: l.color || "",
      imei: l.imei || "", condition_type: l.condition_type || "", sim_type: l.sim_type || "",
      sale_price: l.sale_price ?? "", owner_cost: l.owner_cost ?? "", settlement_mode: l.settlement_mode || "",
      profit_share_pct: l.profit_share_pct ?? "",
    })),
  }));
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const canEdit = op.approval_status === "PENDIENTE_APROBACION";

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setForm((p) => ({ ...p, lines: p.lines.map((ln, idx) => (idx === i ? { ...ln, [k]: v } : ln)) }));
  const num = (v) => (v === "" || v == null ? undefined : Number(v));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        customer_name: form.customer_name || undefined, customer_phone: form.customer_phone || undefined,
        customer_doc: form.customer_doc || undefined, payment_method: form.payment_method || undefined,
        sale_date: form.sale_date || undefined, notes: form.notes || undefined,
      };
      if (isTI) {
        body.agreed_value = num(form.agreed_value);
      } else {
        body.lines = form.lines.map((ln) => {
          const o = { source: ln.source, id: ln.id };
          if (ln.source === "accessory") { o.sale_price = num(ln.sale_price); return o; }
          o.sale_price = num(ln.sale_price);
          if (ln.model) o.model = ln.model; if (ln.capacity) o.capacity = ln.capacity; if (ln.color) o.color = ln.color;
          if (ln.imei) o.imei = ln.imei; if (ln.condition_type) o.condition_type = ln.condition_type; if (ln.sim_type) o.sim_type = ln.sim_type;
          if (ln.source === "new") { o.owner_cost = num(ln.owner_cost); if (ln.settlement_mode) o.settlement_mode = ln.settlement_mode; o.profit_share_pct = num(ln.profit_share_pct); }
          return o;
        });
      }
      await adminApi.patch(`/admin/approvals/${op.operation_id}`, body);
      toast.success("Correcciones guardadas");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    if (!window.confirm("¿Aprobar esta operación? Se reflejará para el consignador.")) return;
    setBusy(true);
    try { await adminApi.post(`/admin/approvals/${op.operation_id}/approve`, {}); toast.success("Operación aprobada"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aprobar"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt("Motivo del rechazo (obligatorio):");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("El motivo es obligatorio"); return; }
    setBusy(true);
    try { await adminApi.post(`/admin/approvals/${op.operation_id}/reject`, { reason: reason.trim() }); toast.success("Operación rechazada"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="approval-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{isTI ? "Trade-In" : "Venta"} {op.operation_id}
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${BADGE[op.approval_status]}`}>{op.approval_status?.replace("_", " ")}</span>
          </DialogTitle>
          <DialogDescription>Registrada por {op.registered_by || "—"} · {fmt(op.registered_at)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Cliente */}
          <section className="rounded-xl border border-zinc-200 p-3 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Cliente</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div><Label className="text-[11px]">Nombre</Label><Input value={form.customer_name} onChange={(e) => setF("customer_name", e.target.value)} disabled={!canEdit} data-testid="appr-customer-name" className="h-9" /></div>
              <div><Label className="text-[11px]">Teléfono</Label><Input value={form.customer_phone} onChange={(e) => setF("customer_phone", e.target.value)} disabled={!canEdit} className="h-9" /></div>
              <div><Label className="text-[11px]">Documento</Label><Input value={form.customer_doc} onChange={(e) => setF("customer_doc", e.target.value)} disabled={!canEdit} className="h-9" /></div>
            </div>
          </section>

          {/* Trade-in valor */}
          {isTI && (
            <section className="rounded-xl border border-zinc-200 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Trade-In</p>
              {(op.lines || []).map((l, i) => (
                <p key={i} className="text-[12px] text-zinc-600">{l.label}{l.imei ? ` · IMEI ${l.imei}` : ""}</p>
              ))}
              <div className="max-w-[200px]"><Label className="text-[11px]">Valor acordado</Label><Input type="number" value={form.agreed_value} onChange={(e) => setF("agreed_value", e.target.value)} disabled={!canEdit} data-testid="appr-agreed-value" className="h-9" /></div>
            </section>
          )}

          {/* Líneas de venta */}
          {!isTI && form.lines.map((ln, i) => (
            <section key={`${ln.source}-${ln.id}`} className="rounded-xl border border-zinc-200 p-3 space-y-3" data-testid={`approval-line-${ln.source}-${ln.id}`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{ln.source === "accessory" ? "Accesorio" : ln.source === "new" ? "iPhone nuevo / consignado" : "iPhone usado"} · {ln.id}</p>
              {ln.source !== "accessory" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div><Label className="text-[11px]">Modelo</Label><Input value={ln.model} onChange={(e) => setLine(i, "model", e.target.value)} disabled={!canEdit} className="h-9" /></div>
                  <div><Label className="text-[11px]">Capacidad</Label><Input value={ln.capacity} onChange={(e) => setLine(i, "capacity", e.target.value)} disabled={!canEdit} className="h-9" /></div>
                  <div><Label className="text-[11px]">Color</Label><Input value={ln.color} onChange={(e) => setLine(i, "color", e.target.value)} disabled={!canEdit} className="h-9" /></div>
                  <div><Label className="text-[11px]">IMEI</Label><Input value={ln.imei} onChange={(e) => setLine(i, "imei", e.target.value)} disabled={!canEdit} className="h-9" /></div>
                  <div><Label className="text-[11px]">Condición</Label><Input value={ln.condition_type} onChange={(e) => setLine(i, "condition_type", e.target.value)} disabled={!canEdit} className="h-9" /></div>
                  <div><Label className="text-[11px]">SIM</Label><Input value={ln.sim_type} onChange={(e) => setLine(i, "sim_type", e.target.value)} disabled={!canEdit} className="h-9" /></div>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div><Label className="text-[11px]">Precio{ln.source === "accessory" ? " unit." : ""}</Label><Input type="number" value={ln.sale_price} onChange={(e) => setLine(i, "sale_price", e.target.value)} disabled={!canEdit} data-testid={`appr-price-${ln.id}`} className="h-9" /></div>
                {ln.source === "new" && <>
                  <div><Label className="text-[11px]">Costo dueño</Label><Input type="number" value={ln.owner_cost} onChange={(e) => setLine(i, "owner_cost", e.target.value)} disabled={!canEdit} className="h-9" /></div>
                  <div><Label className="text-[11px]">Modalidad</Label>
                    <select value={ln.settlement_mode} onChange={(e) => setLine(i, "settlement_mode", e.target.value)} disabled={!canEdit} className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm">
                      <option value="">—</option>
                      {MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                  </div>
                  <div><Label className="text-[11px]">% ganancia</Label><Input type="number" value={ln.profit_share_pct} onChange={(e) => setLine(i, "profit_share_pct", e.target.value)} disabled={!canEdit} className="h-9" /></div>
                </>}
              </div>
            </section>
          ))}

          {/* Pago / notas */}
          <section className="rounded-xl border border-zinc-200 p-3 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Pago y notas</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div><Label className="text-[11px]">Método de pago</Label><Input value={form.payment_method} onChange={(e) => setF("payment_method", e.target.value)} disabled={!canEdit} className="h-9" /></div>
              <div><Label className="text-[11px]">Fecha</Label><Input type="date" value={form.sale_date} onChange={(e) => setF("sale_date", e.target.value)} disabled={!canEdit} className="h-9" /></div>
            </div>
            <div><Label className="text-[11px]">Notas</Label><Textarea value={form.notes} onChange={(e) => setF("notes", e.target.value)} disabled={!canEdit} rows={2} /></div>
          </section>

          {op.approval_status === "RECHAZADA" && op.rejection_reason && (
            <p className="text-sm text-rose-600">Motivo de rechazo: {op.rejection_reason}</p>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} className="h-10">Cerrar</Button>
          {canEdit && <>
            <Button variant="outline" onClick={save} disabled={saving || busy} className="h-10" data-testid="approval-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar correcciones"}</Button>
            <Button variant="outline" onClick={reject} disabled={busy || saving} className="h-10 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="approval-reject"><XCircle className="w-4 h-4 mr-1" /> Rechazar</Button>
            <Button onClick={approve} disabled={busy || saving} className="h-10 bg-emerald-600 hover:bg-emerald-700" data-testid="approval-approve"><CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar</Button>
          </>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
